import {
  Scene,
  Cache,
  WebGLRenderer,
  PerspectiveCamera,
  CameraHelper,
  DirectionalLight,
  DirectionalLightHelper,
  sRGBEncoding
} from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import App from '../app'

/**
 * A class used to initialize everything needed to start a WebGL context across the app.
 *
 * @param {Object} props - An object containing parameters properties.
 * @param {Boolean} props.debug - A boolean defining whether the webgl should be in debug mode or not (activating gui, OrbitControls and some other
 *   helpers).
 * @param {Object} props.rendererOptions - An object with all the options that will be passed to the `WebGLRenderer`.
 *
 * @class
 */
export default class WebGLManager {
  constructor({ debug, rendererOptions } = {}) {
    this.debug = debug !== undefined ? debug : !!window.DEV
    this.isRendering = false
    this.isReady = false
    this.isActive = false
    this.isVisible = false
    this.currentPageGL = null
    this.onIntersect = this.onIntersect.bind(this)

    WebGLManager.instance = this
    WebGLManager.isOrbit = false

    Cache.enabled = true

    this.createRenderer(rendererOptions)
    this.createScene()
    this.createCamera()
    this.createLights()

    this.currentCamera = this.camera
    WebGLManager.camera = this.currentCamera

    if (this.debug) this.debugMode()
    if (window.DEV) window.DEV.getDebugInfo = this.getDebugInfo.bind(this)
  }

  /**
   * Creates the `THREE.WebGLRenderer`.
   *
   * Adds the canvas to the document and watches it's visiblity.
   *
   * @param {Object} options An object that will be passed to the `WebGLRenderer` constructor.
   *
   * @returns {void}
   */
  createRenderer(options = {}) {
    this.renderer = new WebGLRenderer(options)

    // this.renderer.setClearColor(0xffffff)
    this.renderer.physicallyCorrectLights = true
    this.renderer.outputEncoding = sRGBEncoding
    this.renderer.setSize(App.windowWidth, App.windowHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    this.renderer.domElement.classList.add('hidden', 'z-0', 'fixed', 'inset-0', 'pointer-events-none')

    App.domWrapper.prepend(this.renderer.domElement)

    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: '0px',
      threshold: 0
    })

    this.observer.observe(this.renderer.domElement)
  }

  /**
   * Called whenever the canvas goes inside or outside of the viewport.
   *
   * Changes the value `this.isVisible` accordingly.
   *
   * @param {Array<IntersectionObserverEntry>} entries - The related entries.
   * @param {String} observer - The IntersectionObserver instance.
   *
   * @returns {void}
   */
  onIntersect(entries, observer) {
    if (!entries.length) return

    this.isVisible = entries[0].isIntersecting
  }

  /**
   * Creates a new `THREE.Scene`.
   *
   * @returns {void}
   */
  createScene() {
    this.scene = new Scene()
  }

  /**
   * Creates a new `THREE.PerspectiveCamera` and adds it to the Scene.
   *
   * @returns {void}
   */
  createCamera() {
    this.camera = new PerspectiveCamera(45, App.windowWidth / App.windowHeight, 0.1, 500)

    this.camera.position.z = 15
    WebGLManager.unitSize = WebGLManager.getUnitSize()

    this.scene.add(this.camera)
  }

  /**
   * Creates an object with some lights and adds them to the Scene.
   *
   * @returns {void}
   */
  createLights() {
    this.lights = {
      directional: new DirectionalLight(0xffffff, 1)
    }

    this.scene.add(this.lights.directional)
  }

  /**
   * A method to call when the WebGL context is created and ready to run.
   *
   * Sets `this.isReady = true`.
   *
   * Immediately forces a render of the scene with the active Camera.
   *
   * Called by `App` in its `start()` method.
   *
   * @static
   * @returns {void}
   */
  static ready() {
    WebGLManager.instance.ready()
  }

  /**
   * A method to call when the WebGL context is created and ready to run.
   *
   * Sets `this.isReady = true`.
   *
   * Immediately forces a render of the scene with the active Camera.
   *
   * Called by `App` in its `start()` method.
   *
   * @returns {void}
   */
  ready() {
    this.isReady = true

    this.renderer.render(this.scene, this.currentCamera)
  }

  /**
   * Sets a PageGL instance as the new `this.currentPageGL`.
   *
   * @param {PageGL} pageGL - A PageGL instance which will be set as the new `currentPageGL`.
   *
   * @static
   * @returns {void}
   */
  static setCurrentPage(pageGL) {
    if (!pageGL) return

    WebGLManager.instance.currentPageGL = pageGL
  }

  /**
   * Gets the current pageGL.
   *
   * @static
   * @returns {(PageGL|null)} The value of `this.currentPageGL`.
   */
  static getCurrentPage() {
    return WebGLManager.instance.currentPageGL
  }

  /**
   * Sets `this.isActive = true`.
   *
   * Shows the canvas.
   *
   * @private Called by `PageGL` after loading the assets, at the end of the `prepare()` method.
   * @static
   * @returns {void}
   */
  static activate() {
    WebGLManager.instance.isActive = true
    WebGLManager.instance.renderer.domElement.classList.remove('hidden')
  }

  /**
   * Sets `this.isActive = false`.
   *
   * Sets `this.currentPageGL = null`.
   *
   * Hides the canvas.
   *
   * Forces a new render of the scene using the current Camera.
   *
   * @param {Boolean} render - Whether to force a new render of the scene or not (Default is `true`).
   *
   * @private Called by `Page` constructor if the current page doesn't have any PageGL related to it.
   * @static
   * @returns {void}
   */
  static disactivate(render = true) {
    WebGLManager.instance.isActive = false
    WebGLManager.instance.currentPageGL = null

    WebGLManager.instance.renderer.domElement.classList.add('hidden')

    if (!render) WebGLManager.instance.renderer.render(WebGLManager.instance.scene, WebGLManager.instance.currentCamera)
  }

  /**
   * Loads current page's gl assets.
   *
   * @returns {Promise} A Promise resolved when all needed items are loaded.
   */
  loadFirstPage() {
    // If the first page has a PageGL instanced attached to it,
    // Load its assets, then tell the WebGLManager that it's ready to go.
    // NOTE: this is the place where you can add a loading animation and end it as soon as the `prepare()` method has returned.
    if (App.currentRenderer.pageGL) {
      return App.currentRenderer.pageGL.prepare()
        .then(() => {
          // eslint-disable-next-line no-undef
          WebGLManager.setCurrentPage(App.currentRenderer.pageGL)
          // eslint-disable-next-line no-undef
          WebGLManager.ready()

          return Promise.resolve()
        })
    } else {
      WebGLManager.ready()

      return Promise.resolve()
    }
  }

  /**
   * Checks if a new PageGL needs to be initialized,
   * also checks if a PageGL is already running, and leaves it if needed.
   *
   * If no PageGL is linked to this page, disactivate the WebGL context.
   *
   * @param {Page} page - The Page instance to check.
   *
   * @returns {void}
   */
  checkWebGLForPage(page) {
    if (!WebGLManager.instance) return
    
    // Gets the current PageGL (if any).
    const currentGL = WebGLManager.getCurrentPage()

    if (page.props.webgl) {
      // If a PageGL is linked to this Page, initialize it.
      page.pageGL = new page.props.webgl({ page })

      // If the app already had a PageGL running and its name is not the same (= not the same template).
      if (currentGL && currentGL.name !== page.pageGL.name) {
        // Leave the last page.
        currentGL.onLeave()
        // Set the new page as the current page.
        WebGLManager.setCurrentPage(page.pageGL)
      }
    } else if (currentGL) {
      // If there isn't any PageGL linked to this page, AND if the app already had a PageGL running, leave it.
      currentGL.onLeave()
      // Disactivate the WebGLManager.
      WebGLManager.disactivate()
    } else WebGLManager.disactivate(false)
  }

  /**
   * Adds objects to the scene.
   *
   * @param  {...Object3D} objects The Object3D instances you want to add to the scene.
   *
   * @static
   * @returns {void}
   */
  static addToScene(...objects) {
    if (objects) WebGLManager.instance.scene.add(...objects)
  }

  /**
   * Adds an object to the Camera.
   *
   * @param {...Object3D} objects The Object3D instances to add to the Camera.
   *
   * @static
   * @returns {void}
   */
  static addToCamera(...objects) {
    if (objects) WebGLManager.instance.camera.add(...objects)
  }

  /**
   * Removes objects from the scene.
   *
   * @param {(Object3D|Array<Object3D>)} objects The Object3D that will be removed from the scene.
   *
   * @static
   * @returns {void}
   */
  static removeFromScene(objects) {
    if (Array.isArray(objects)) {
      for (const obj of objects) {
        WebGLManager.removeObject(obj)
      }
    } else WebGLManager.removeObject(objects)
  }

  /**
   * Removes a single object from the scene.
   *
   * @param {Object3D} obj The Object3D that will be removed from the scene.
   *
   * @static
   * @returns {void}
   */
  static removeObject(obj) {
    if (!obj) return

    WebGLManager.instance.scene.remove(obj)

    obj.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) child.material.dispose()
    })
  }

  /**
   * Gets the canvas DOM element.
   *
   * @static
   * @returns {Element} The canvas.
   */
  static getCanvas() {
    return WebGLManager.instance.renderer.domElement
  }

  /**
   * Gets the main Camera.
   *
   * @static
   * @returns {Camera} The Camera.
   */
  static getCamera() {
    return WebGLManager.instance.camera
  }

  /**
   * Forces a new render of the Scene using the current Camera.
   *
   * @static
   * @returns {void}
   */
  static render() {
    WebGLManager.instance.renderer.render(WebGLManager.instance.scene, WebGLManager.instance.currentCamera)
  }

  /**
   * Returns the instance's `isRendering` property.
   *
   * @returns {Boolean} Whether the context is rendering or not.
   */
  static isRendering() {
    return WebGLManager.instance.isRendering
  }

  /**
   * Renders a new frame only if **ALL** of the following conditions are **true** :
   *
   * - `this.isReady === true` - The context is ready.
   * - `this.isActive === true` - The context is active.
   * - `this.isVisible === true` - The canvas is visible on the viewport.
   * - `this.currentPageGL !== null` - A PageGL instance is exists.
   * - `this.currentPageGL.disabled === false` - The PageGL instance is not disabled.
   *
   * **THEN** it will call `this.currentPageGL.update()` and render the frame with the current Camera.
   *
   * @param {Object} params - An object containing two properties related to the elapsed time.
   * @param {Number} params.time - The elapsed time.
   * @param {Number} params.deltaTime - The difference of time between the previous frame and the current.
   *
   * @returns {void}
   */
  render({ time, deltaTime }) {
    if (!this.isReady || !this.isActive || !this.isVisible || !this.currentPageGL || (this.currentPageGL && this.currentPageGL.disabled)) {
      if (this.isRendering) {
        this.isRendering = false

        if (this.currentPageGL) this.currentPageGL.disactivated()
      }

      return
    } else if (!this.isRendering) {
      this.isRendering = true

      if (this.currentPageGL) this.currentPageGL.activated()
    }

    this.currentPageGL.render({
      time,
      deltaTime
    })
    this.renderer.render(this.scene, this.currentCamera)
  }

  /**
   * A method called when the cursor moves.
   *
   * Calls `this.currentPageGL.pointermove()`.
   *
   * Called by `App`.
   *
   * @param {PointerEvent} e The pointer event.
   *
   * @returns {void}
   */
  pointermove(e) {
    if (this.currentPageGL) this.currentPageGL.pointermove(e)
  }

  /**
   * A method called when the user scrolls.
   *
   * Calls `this.currentPageGL.scroll()`.
   *
   * Called by `App` (this method is throttled and called every 200ms).
   *
   * @returns {void}
   */
  scroll() {
    if (this.currentPageGL) this.currentPageGL.scroll()
  }

  static getUnitSize(depth = 0) {
    const cam = WebGLManager.getCamera()
    const offset = cam.position.z
    let d = depth

    if (depth < offset) d -= offset
    else d += offset

    const vFOV = (cam.fov * Math.PI) / 180 // vertical fov in radians

    // Math.abs to ensure the result is always positive
    const height = 2 * Math.tan(vFOV / 2) * Math.abs(d)
    const width = height * cam.aspect

    return {
      height,
      width
    }
  }

  /**
   * Called when the browser is resized.
   *
   * Adapts the Camera aspects and set a new size for the Renderer.
   *
   * Called by `App` (this method is throttled and called every 200ms).
   *
   * @returns {void}
   */
  resize() {
    this.camera.aspect = App.windowWidth / App.windowHeight
    WebGLManager.unitSize = WebGLManager.getUnitSize()
    this.camera.updateProjectionMatrix()

    if (this.debugCamera) {
      this.debugCamera.aspect = App.windowWidth / App.windowHeight
      this.debugCamera.updateProjectionMatrix()
    }

    this.renderer.setSize(App.windowWidth, App.windowHeight)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    if (this.currentPageGL) this.currentPageGL.resize()
  }

  /**
   * Turns on the debug mode (shows debug info, creates debug camera and orbit controls, debug lights and GUI).
   *
   * Called by the `constructor` if its `debug` parameter property is set to `true`.
   *
   * @returns {void}
   */
  debugMode() {
    this.createDebugCamera()
    this.createDebugLights()
    this.createDebugGUI()
  }

  /**
   * Creates an `THREE.OrbitControls` using a new `THREE.PerspectiveCamera`.
   *
   * Adds a new `THREE.CameraHelper` to the Scene.
   *
   * @returns {void}
   */
  createDebugCamera() {
    this.debugCamera = new PerspectiveCamera(45, App.windowWidth / App.windowHeight, 0.1, 10000)
    this.controls = new OrbitControls(this.debugCamera, App.domWrapper)
    this.cameraHelper = new CameraHelper(this.camera)

    this.debugCamera.position.set(0, 0, 17)

    this.cameraHelper.visible = false
    this.controls.enabled = false

    this.scene.add(this.cameraHelper)
  }

  /**
   * Creates helpers for lights and adds them to the Scene.
   *
   * @returns {void}
   */
  createDebugLights() {
    this.directionalLightHelper = new DirectionalLightHelper(this.lights.directional, 5, 0x00ff00)

    this.scene.add(this.directionalLightHelper)
  }

  /**
   * Creates the global GUI instance that will be used by every PageGL instance if the debug mode is on.
   *
   * The default GUI contains some Camera parameters (position, resetting, orbit, helpers).
   *
   * @returns {void}
   */
  createDebugGUI() {
    if (window.DEV) {
      window.DEV.gl = WebGLManager

      if (window.DEV.gui) window.DEV.gui.createWebGLGUI(this)
    }
  }

  /**
   * Logs the webgl information in the console.
   *
   * Can be call via `window.DEV.getDebugInfo()`.
   *
   * @returns {void}
   */
  getDebugInfo() {
    console.table({
      'Draw calls': this.renderer.info.render.calls,
      'Geometries': this.renderer.info.memory.geometries,
      'Textures': this.renderer.info.memory.textures,
      'Programs': this.renderer.info.programs.length,
      'Raf running ?': this.isRendering,
      'Is ready ?': this.isReady,
      'Is active ?': this.isActive,
      'Is visible ?': this.isVisible,
      'Has PageGL ?': !!this.currentPageGL,
      'Is PageGL active ?': this.currentPageGL && !this.currentPageGL.disabled
    })
  }
}
